import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Text, Button, Box, InlineStack, Select, BlockStack, RangeSlider, Modal, Banner } from '@shopify/polaris';
import useAxiosClient from '../axiosClient';
import useTrialOffer from '../../utils/trialOffer';
import PricingHeader from './PricingHeader';
import PricingTrustFaq from './PricingTrustFaq';

/*
 * Pricing page (universal / Stripe).
 *
 * This is DESIGN ONLY for now. Every purchase button funnels through
 * `handleSubscribe` / `handleCustomSubscribe`, which are intentionally stubbed.
 * Wire these up to Stripe later (see the TODO inside handleSubscribe) — e.g.
 * create a Checkout Session on the backend and redirect to session.url, or open
 * a Stripe Payment Link for the selected plan.
 */

// Reusable pricing card -------------------------------------------------------
/** Small filled star for the Most Popular flag. Fills with currentColor. */
const StarGlyph = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

// The card titles arrive as ready-made nodes (icon + <Text headingXl>). This
// shrinks them to strip size without touching where they come from.
const SMALL_TITLE =
  'inline-flex items-center [&_h3]:!text-[13.5px] [&_h3]:!font-semibold [&_h3]:!leading-none [&_svg]:h-3.5 [&_svg]:w-3.5 [&_.flex]:gap-1.5';

const PricingCard = ({
  title,
  description,
  price,
  features,
  featuredText,
  button,
  frequency,
  annualSavings = null,
  seatsIncluded = null,
  billedAnnuallyPrice,
  billingType = null,
  onStaffCountChange = null,
  staffCount = 10,
  onStandardStaffCountChange = null,
  standardStaffCount = 5,
  onEnterpriseStaffCountChange = null,
  enterpriseStaffCount = 100,
  isCurrentPlan = false,
  // Rendered under the CTA. Only the current plan passes one, and only
  // while there is a subscription to cancel.
  secondaryAction = null,
  t,
}) => {
  const isFeatured = featuredText === 'Most Popular';
  const isMint = isFeatured;

  return (
    <div
      className="relative flex w-full flex-col overflow-hidden rounded-[12px] border transition-shadow duration-200 hover:shadow-md"
      style={{
        backgroundColor: isMint ? '#f6fbf8' : '#ffffff',
        borderColor: isMint ? 'rgba(0,128,96,0.35)' : '#e3e3e3',
        boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
      }}
    >
      {/* Strip: every card names its own plan on the left, so the three headers
          read as a set and each price row starts on the same line. The featured
          card adds the Most Popular badge on the right - a flag ABOUT the plan,
          not a replacement for its name and not a pill floating over the border. */}
      <div
        className="flex items-center justify-between gap-2 px-5 text-[12px] font-semibold"
        style={{
          minHeight: 38,
          color: isMint ? '#008060' : '#303030',
          backgroundColor: isMint ? 'rgba(0,128,96,0.07)' : '#f7f7f7',
        }}
      >
        <span className={SMALL_TITLE}>{title}</span>

        {isFeatured && (
          <span
            className="inline-flex flex-shrink-0 items-center gap-1 rounded-full px-2 py-[3px] text-[11px] font-bold leading-none"
            style={{ backgroundColor: '#008060', color: '#ffffff' }}
          >
            <StarGlyph />
            {featuredText}
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col p-6">

      {/* Price */}
      <div className={`pb-0 border-b border-dashed ${isMint ? 'border-emerald-900/10' : 'border-slate-200'}`}>
        <div className="flex items-baseline gap-1 justify-start">
          <span className="text-3xl font-bold tracking-tight text-slate-900">{price}</span>
          {frequency && (
            <span className="text-xs font-semibold mb-1 uppercase tracking-wide text-slate-500">/{frequency.replace('Per ', '').toLowerCase()}</span>
          )}
        </div>

        {/* The rate note sits UNDER the price now, as on the Shopify cards: the
            price is the decision, so nothing gets between it and the plan name. */}
        {description && (
          <div className="text-xs font-medium text-slate-500 mt-1">{description}</div>
        )}

        {billedAnnuallyPrice && (
          <div className="text-xs font-medium text-slate-500 mt-1">Billed {billedAnnuallyPrice} annually</div>
        )}

        {/* Per seat. $39.99/month reads as expensive; $4.00 per employee per
            month reads against what that employee costs per hour. Divided by the
            seats actually being bought, so it tracks the seat selector. */}
        {(() => {
          const amount = Number(String(price).replace(/[^0-9.]/g, ''));
          const seats = Number(seatsIncluded);

          if (!Number.isFinite(amount) || amount <= 0 || !Number.isFinite(seats) || seats <= 0) {
            return null;
          }

          return (
            <div className="text-xs text-slate-500 mt-1">
              {`About $${(amount / seats).toFixed(2)} per staff member per month`}
            </div>
          );
        })()}

        {/* People do not feel percentages. A real dollar figure, computed from the
            same annual and monthly functions that build the charge. */}
        {annualSavings > 0 && (
          <div className="mt-1.5">
            <span className="rounded-md px-1.5 py-0.5 text-[11px] font-bold bg-emerald-100 text-emerald-800">
              {`Save $${Number(annualSavings).toFixed(2)} a year`}
            </span>
          </div>
        )}

      </div>

      {/* CTA */}
      <div className="my-4">
        <Button {...button.props} fullWidth size="large">
          <span className="font-bold text-sm">{button.content}</span>
        </Button>

        {secondaryAction ? (
          /* Directly under the button it acts on, so which plan it cancels is
             never in question. Plain and critical rather than a second filled
             block: the way out should be findable, not the loudest thing on
             the card. */
          <div className="mt-2 flex justify-center">
            <Button
              variant="plain"
              tone="critical"
              onClick={secondaryAction.onClick}
              loading={secondaryAction.loading}
            >
              {secondaryAction.content}
            </Button>
          </div>
        ) : null}
      </div>

      {/* Features */}
      <div className="flex-1 space-y-4 mb-2">
        {features?.map((feature, id) => {
          let content;

          if ((billingType === 1 || billingType === 5) && feature.includes('5 Staff')) {
            content = (
              <div className="flex items-center gap-2 flex-wrap">
                <span>Up to</span>
                <div className="w-20">
                  <Select
                    labelInline
                    label=""
                    options={Array.from({ length: 46 }, (_, i) => ({ label: `${i + 5}`, value: `${i + 5}` }))}
                    value={`${standardStaffCount}`}
                    onChange={onStandardStaffCountChange}
                    disabled={isCurrentPlan}
                  />
                </div>
                <span>{t('staff_members')}</span>
              </div>
            );
          } else if ((billingType === 2 || billingType === 6) && feature.includes('10')) {
            content = (
              <div className="flex items-center gap-2 flex-wrap">
                <span>Up to</span>
                <div className="w-20">
                  <Select
                    labelInline
                    label=""
                    options={Array.from({ length: 91 }, (_, i) => ({ label: `${i + 10}`, value: `${i + 10}` }))}
                    value={`${staffCount}`}
                    onChange={onStaffCountChange}
                    disabled={isCurrentPlan}
                  />
                </div>
                <span>{t('staff_members')}</span>
              </div>
            );
          } else if ((billingType === 4 || billingType === 7) && feature.includes('100')) {
            content = (
              <div className="flex items-center gap-2 flex-wrap">
                <span>Up to</span>
                <div className="w-24">
                  <Select
                    labelInline
                    label=""
                    options={Array.from({ length: 401 }, (_, i) => ({ label: `${i + 100}`, value: `${i + 100}` }))}
                    value={`${enterpriseStaffCount}`}
                    onChange={onEnterpriseStaffCountChange}
                    disabled={isCurrentPlan}
                  />
                </div>
                <span>{t('staff_members')}</span>
              </div>
            );
          } else {
            content = <span className="flex-1 text-sm font-medium">{feature.replace('10 Staff Members', '').replace('100 Staff Members', '').trim() || feature}</span>;
          }

          return (
            <div key={id} className="flex items-start gap-3 group/item">
              <div className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${isMint ? 'bg-white text-emerald-600' : 'bg-slate-100 text-slate-600'}`}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              <div className="leading-tight text-sm font-medium text-slate-700">
                {content}
              </div>
            </div>
          );
        })}
      </div>
      </div>
    </div>
  );
};

// Plan icons ------------------------------------------------------------------
const StandardIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
    <circle cx="9.5" cy="9.5" r="5.5"></circle>
    <circle cx="14.5" cy="14.5" r="5.5"></circle>
  </svg>
);
const PremiumIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
  </svg>
);
const EnterpriseIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
    <circle cx="12" cy="12" r="3" fill="currentColor"></circle>
  </svg>
);

const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
};

const initialStoredUser = readStoredUser();
const initialStaffCount = (() => {
  const planId = Number(initialStoredUser?.plan_id);
  const seats = Number(initialStoredUser?.paid_available_staffs);
  if ((planId === 2 || planId === 6) && seats > 0) return Math.max(10, seats);
  return 10;
})();
const initialStandardStaffCount = (() => {
  const planId = Number(initialStoredUser?.plan_id);
  const seats = Number(initialStoredUser?.paid_available_staffs);
  if ((planId === 1 || planId === 5) && seats > 0) return Math.max(5, seats);
  return 5;
})();
const initialEnterpriseStaffCount = (() => {
  const planId = Number(initialStoredUser?.plan_id);
  const seats = Number(initialStoredUser?.paid_available_staffs);
  if ((planId === 4 || planId === 7) && seats > 0) return Math.max(100, seats);
  return 100;
})();
const initialBillingInterval = (() => {
  const planId = Number(initialStoredUser?.plan_id);

  // Someone already subscribed must land on their OWN interval. Put a monthly
  // subscriber on the annual tab and effectiveBillingType resolves to 5/6/7,
  // isCurrentPlan finds no match against their plan_id, and the page offers them
  // a subscribe button for the plan they already pay for.
  if ([5, 6, 7].includes(planId)) return 'annual';
  if ([1, 2, 3, 4, 8].includes(planId)) return 'monthly';

  // Nobody subscribed yet: annual is a real 20% off, shows the lower headline
  // number, and books twelve months instead of one.
  return 'annual';
})();
const initialCustomStaffCount = (() => {
  const planId = Number(initialStoredUser?.plan_id);
  const seats = Number(initialStoredUser?.paid_available_staffs);
  if (planId === 8 && seats > 0) return seats;
  return 2;
})();

const Pricing = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const axiosClient = useAxiosClient();

  const [billingInterval, setBillingInterval] = useState(initialBillingInterval);
  const [activatingFree, setActivatingFree] = useState(false);
  const [staffCount, setStaffCount] = useState(initialStaffCount);
  const [standardStaffCount, setStandardStaffCount] = useState(initialStandardStaffCount);
  const [enterpriseStaffCount, setEnterpriseStaffCount] = useState(initialEnterpriseStaffCount);
  const [customStaffCount, setCustomStaffCount] = useState(initialCustomStaffCount);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [canceling, setCanceling] = useState(false);
  // One shared rule - see utils/trialOffer. The seat wall and the setup guide
  // label their buttons from the same call, and StripeController applies the
  // same test before granting the trial.
  const { trialDays } = useTrialOffer(axiosClient);

  // Confirmation state. Errors live on the modal rather than in a toast: a
  // toast fires as the dialog closes, so a failed cancel reads as a successful
  // one.
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [downgradeOpen, setDowngradeOpen] = useState(false);
  const [downgradeError, setDowngradeError] = useState(null);

  // The shop's current plan state (drives "Current Plan" buttons + cancel control).
  const refreshCurrentUser = () => {
    axiosClient.get('/user')
      .then(({ data }) => setCurrentUser(data || null))
      .catch(() => {});
  };

  /** The label the checkout will actually honour. */
  const planButtonText = (key) => {
    const days = trialDays(key);

    return days > 0
      ? t('seatwall_start_trial', { days })
      : t('seatwall_subscribe');
  };

  // Keep staff selectors and billing toggle aligned with the active subscription.
  useEffect(() => {
    if (!currentUser?.stripe_subscription_id) return;

    const seats = Number(currentUser.paid_available_staffs);
    const planId = Number(currentUser.plan_id);

    if (planId === 1 || planId === 5) {
      setStandardStaffCount(Math.max(5, seats || 5));
    } else if (planId === 2 || planId === 6) {
      setStaffCount(Math.max(10, seats || 10));
    } else if (planId === 4 || planId === 7) {
      setEnterpriseStaffCount(Math.max(100, seats || 100));
    } else if (planId === 8 && seats > 0) {
      setCustomStaffCount(seats);
    }

    if ([5, 6, 7].includes(planId)) {
      setBillingInterval('annual');
    } else if ([1, 2, 4, 8].includes(planId)) {
      setBillingInterval('monthly');
    }
  }, [currentUser]);

  // Handle the redirect back from Stripe Checkout, then (re)load the plan state.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');

    if (sessionId && params.get('success') === 'true') {
      axiosClient.post('/stripe/verify-session', { session_id: sessionId })
        .then(({ data }) => {
          shopify.toast.show(data.message || 'Subscription activated!');
          if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
        })
        .catch((e) => shopify.toast.show(e.response?.data?.message || 'Could not verify payment.', { error: true }))
        .finally(() => {
          window.history.replaceState({}, document.title, '/Pricing');
          refreshCurrentUser();
        });
    } else {
      if (params.get('canceled') === 'true') {
        shopify.toast.show('Checkout canceled.');
        window.history.replaceState({}, document.title, '/Pricing');
      }
      refreshCurrentUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Derived plan state.
  const hasSubscription = !!currentUser?.stripe_subscription_id;
  const isFreeActive = !!currentUser && !hasSubscription
    && (currentUser.shopify_freemium == 1 || currentUser.plan_id == null);
  const isCustomPlanActive = hasSubscription && Number(currentUser?.plan_id) === 8;

  const handleStaffCountChange = useCallback((value) => setStaffCount(Number(value)), []);
  const handleStandardStaffCountChange = useCallback((value) => setStandardStaffCount(Number(value)), []);
  const handleEnterpriseStaffCountChange = useCallback((value) => setEnterpriseStaffCount(Number(value)), []);

  // Price calculations (local — no backend) ----------------------------------
  const calculateStandardPrice = () => 24.99 + Math.max(0, standardStaffCount - 5) * 5;
  const calculatePremiumPrice = () => 39.99 + Math.max(0, staffCount - 10) * 4;
  const calculateEnterprisePrice = () => 199.99 + Math.max(0, enterpriseStaffCount - 100) * 2;
  const calculateAnnualStandardPrice = () => 239.90 + Math.max(0, standardStaffCount - 5) * 48; // $4/mo * 12
  const calculateAnnualPremiumPrice = () => 383.90 + Math.max(0, staffCount - 10) * 38.40; // $3.20/mo * 12
  const calculateAnnualEnterprisePrice = () => 1919.90 + Math.max(0, enterpriseStaffCount - 100) * 19.20; // $1.60/mo * 12

  // Start a Stripe Checkout Session for a paid plan, then redirect to it.
  const startCheckout = async (plan, key, seats) => {
    setCheckoutLoading(key);
    try {
      const { data } = await axiosClient.post('/stripe/create-checkout-session', {
        plan,
        interval: billingInterval,
        staffCount: seats,
      });
      if (data?.url) {
        window.location.href = data.url; // hosted Stripe Checkout
        return;
      }
      shopify.toast.show(data?.message || 'Could not start checkout.', { error: true });
    } catch (e) {
      shopify.toast.show(e.response?.data?.message || 'Could not start checkout.', { error: true });
    }
    setCheckoutLoading(null);
  };

  const handleSubscribe = (planName) => {
    const key = String(planName).toLowerCase(); // 'standard' | 'premium' | 'enterprise'
    const seats = key === 'standard' ? standardStaffCount : key === 'premium' ? staffCount : key === 'enterprise' ? enterpriseStaffCount : undefined;
    startCheckout(key, key, seats);
  };

  const handleCustomSubscribe = () => {
    if (isCustomPlanActive) return;
    startCheckout('custom', 'custom', customStaffCount);
  };

  const openCancelModal = () => {
    setCancelError(null);
    setCancelModalOpen(true);
  };

  const handleCancelSubscription = async () => {
    setCanceling(true);
    setCancelError(null);
    try {
      const { data } = await axiosClient.post('/stripe/cancel-subscription');
      shopify.toast.show(data.message || 'Subscription canceled.');
      if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
      setCancelModalOpen(false);
      refreshCurrentUser();
    } catch (e) {
      // Stays open, carrying the reason. Closing on failure would tell the user
      // their subscription is gone while Stripe carries on billing it.
      setCancelError(e.response?.data?.message || 'Could not cancel subscription. Please try again.');
    } finally {
      setCanceling(false);
    }
  };

  const openDowngradeModal = () => {
    setDowngradeError(null);
    setDowngradeOpen(true);
  };

  // The free plan isn't billed through Stripe — activate it directly.
  const handleActivateFree = async () => {
    setActivatingFree(true);
    setDowngradeError(null);
    try {
      await axiosClient.post('/auth/activate-free-plan');
      shopify.toast.show('Free plan activated — you have 1 staff seat.');
      setDowngradeOpen(false);
      setTimeout(() => navigate('/'), 900);
    } catch (e) {
      // 409 when a Stripe subscription is still live - the server refuses
      // rather than silently capping a paying account at one seat. Its message
      // says so, so show it instead of a generic failure.
      setDowngradeError(
        e.response?.data?.message || 'Could not activate the free plan. Please try again.'
      );
    } finally {
      setActivatingFree(false);
    }
  };

  // Plan data -----------------------------------------------------------------
  const starter = {
    planName: 'Starter',
    features: [
      'Up to 1 Staff Member',
      'POS Clock-In/Out Events',
      'Scheduling Shifts',
    ],
  };

  const mainPlans = [
    {
      planName: 'Standard',
      title: <div className="flex items-center gap-2">{StandardIcon}<Text variant="headingXl" as="h3">{t('standard_plan')}</Text></div>,
      description: '+ $5 per additional staff',
      billingType: 1,
      buttonText: planButtonText('standard'),
      features: [
        'Up to 5 Staff Members',
        'Mobile & POS Clock-In/Out Events',
        'Scheduling Shifts',
        'Breaks & Overtime',
        'Payroll Management',
        'Tasklist Management',
        'Detailed Reporting',
        'Live Chat',
      ],
    },
    {
      planName: 'Premium',
      title: <div className="flex items-center gap-2">{PremiumIcon}<Text variant="headingXl" as="h3">{t('premium_plan')}</Text></div>,
      description: '+ $4 per additional staff',
      featuredText: 'Most Popular',
      billingType: 2,
      // The trial is real now: createCheckoutSession sets
      // subscription_data.trial_period_days from the same plans row this label
      // is derived from, and only for an account that has never subscribed.
      // planButtonText says "Subscribe" whenever either of those is false.
      buttonText: planButtonText('premium'),
      features: [
        'Up to 10 Staff Members',
        'Desktop & Mobile Time Tracking',
        'Scheduling Shifts',
        'Breaks & Overtime',
        'PTO, Break Policies and Labor Compliance',
        'Payroll Management',
        'Time-Off & Task List Management',
        'Advanced Compliance',
        'Advanced Security Features',
        'Priority Support',
      ],
    },
    {
      planName: 'Enterprise',
      title: <div className="flex items-center gap-2">{EnterpriseIcon}<Text variant="headingXl" as="h3">{t('enterprise_plan')}</Text></div>,
      description: '+ $2 per additional staff',
      billingType: 4,
      // The trial is real now: createCheckoutSession sets
      // subscription_data.trial_period_days from the same plans row this label
      // is derived from, and only for an account that has never subscribed.
      // planButtonText says "Subscribe" whenever either of those is false.
      buttonText: planButtonText('enterprise'),
      features: [
        'Up to 100 Staff Members',
        'Unlimited Time Tracking Selfie Attendance',
        'Breaks & Overtime',
        'Multiple Scheduling Shifts',
        'HR, PTO, Break and Labor Compliance',
        'Payroll Management',
        'Performance Management',
        'Checklists Tasks Management',
        'Time off policies & PTO Accrual',
        'Adaptive Reporting & Insights (ADP)',
        'Strategic Account Manager',
      ],
    },
  ];

  // Which plan id a card is offering RIGHT NOW - it changes with the billing
  // toggle, and so does whether a card calls itself the current plan.
  const shownBillingType = (card) => billingInterval === 'annual'
    ? (card.billingType === 1 ? 5 : card.billingType === 2 ? 6 : 7)
    : card.billingType;

  const planId = Number(currentUser?.plan_id);

  // Named for the cancel dialog. Matches on either billing period, since the
  // name is the same whichever one the toggle happens to be showing.
  const currentPlanName = isCustomPlanActive ? 'Custom' : (
    mainPlans.find((card) => {
      const annualType = card.billingType === 1 ? 5 : card.billingType === 2 ? 6 : 7;
      return planId === card.billingType || planId === annualType;
    })?.planName || 'current'
  );

  // Is a cancel link actually on screen? A subscriber who flips the toggle to
  // the other billing period sees no card claim to be their current plan, and
  // the same is true of a legacy plan_id that matches no card at all. Either
  // way the fallback below has to carry it, or they are paying with no way to
  // stop.
  const currentPlanHasCancel = hasSubscription && (
    isCustomPlanActive || mainPlans.some((card) => planId === shownBillingType(card))
  );

  return (
    <Box padding={{ xs: '100', sm: '100', md: '0', lg: '0' }}>
      <BlockStack gap="600">
        {/* Branded masthead: icon, wordmark, positioning line, and the one
            credential this product can actually back. */}
        <PricingHeader t={t} />

        {/* Monthly / Yearly toggle */}
        <div className="flex justify-center w-full mb-6">
          <div className="bg-white p-1 rounded-full inline-grid grid-cols-2 relative shadow-inner min-w-[300px]">
            <div
              className="absolute top-1 bottom-1 rounded-full bg-slate-900 shadow-sm transition-all duration-300 ease-in-out z-0"
              style={{
                left: billingInterval === 'monthly' ? '4px' : '50%',
                width: 'calc(50% - 4px)',
              }}
            />
            <button
              onClick={() => setBillingInterval('monthly')}
              className={`relative z-10 px-4 py-2 rounded-full text-sm font-bold transition-colors duration-300 flex items-center justify-center w-full ${billingInterval === 'monthly' ? 'text-white' : 'text-slate-600 hover:text-slate-900'}`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingInterval('annual')}
              className={`relative z-10 px-4 py-2 rounded-full text-sm font-bold transition-colors duration-300 flex items-center justify-center gap-2 w-full ${billingInterval === 'annual' ? 'text-white' : 'text-slate-600 hover:text-slate-900'}`}
            >
              Yearly
              <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide font-bold transition-colors duration-300 ${billingInterval === 'annual' ? 'bg-emerald-500 text-white' : 'bg-emerald-100 text-emerald-700'}`}>
                20% OFF
              </span>
            </button>
          </div>
        </div>

        {/* Main pricing cards: Standard, Premium, Enterprise */}
        <Box className="flex justify-center w-full px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start max-w-6xl mx-auto w-full">
            {mainPlans.map((card, i) => {
              const annual = billingInterval === 'annual';
              const effectiveBillingType = annual
                ? (card.billingType === 1 ? 5 : card.billingType === 2 ? 6 : 7)
                : card.billingType;

              const priceString = (() => {
                if (card.billingType === 1) {
                  const monthly = annual ? calculateAnnualStandardPrice() / 12 : calculateStandardPrice();
                  return '$' + monthly.toFixed(2);
                }
                if (card.billingType === 2) {
                  const monthly = annual ? calculateAnnualPremiumPrice() / 12 : calculatePremiumPrice();
                  return '$' + monthly.toFixed(2);
                }
                const monthly = annual ? calculateAnnualEnterprisePrice() / 12 : calculateEnterprisePrice();
                return '$' + monthly.toFixed(2);
              })();

              const billedAnnuallyPrice = !annual ? null
                : card.billingType === 1 ? ('$' + calculateAnnualStandardPrice().toFixed(2))
                : card.billingType === 2 ? ('$' + calculateAnnualPremiumPrice().toFixed(2))
                : ('$' + calculateAnnualEnterprisePrice().toFixed(2));

              // The old strike-through was hardcoded to the BASE price while the
              // price beside it scales with the seat selector, so Workforce at 50
              // seats showed $159.99 with $39.99 struck through. A real dollar
              // saving replaces it: twelve months at the monthly rate minus the
              // annual total, both for the SAME seat count.
              const annualSavings = !annual ? null
                : card.billingType === 1 ? calculateStandardPrice() * 12 - calculateAnnualStandardPrice()
                : card.billingType === 2 ? calculatePremiumPrice() * 12 - calculateAnnualPremiumPrice()
                : calculateEnterprisePrice() * 12 - calculateAnnualEnterprisePrice();

              const seatsIncluded =
                card.billingType === 1 ? Number(standardStaffCount)
                : card.billingType === 2 ? Number(staffCount)
                : Number(enterpriseStaffCount);

              const isCurrentPlan = currentUser != null && currentUser.plan_id == effectiveBillingType;

              return (
                <PricingCard
                  key={i}
                  title={card.title}
                  description={card.description}
                  price={priceString}
                  frequency="month"
                  features={card.features}
                  featuredText={card.featuredText}
                  annualSavings={annualSavings}
                  seatsIncluded={seatsIncluded}
                  billedAnnuallyPrice={billedAnnuallyPrice}
                  billingType={effectiveBillingType}
                  onStaffCountChange={handleStaffCountChange}
                  staffCount={staffCount}
                  onStandardStaffCountChange={handleStandardStaffCountChange}
                  standardStaffCount={standardStaffCount}
                  onEnterpriseStaffCountChange={handleEnterpriseStaffCountChange}
                  enterpriseStaffCount={enterpriseStaffCount}
                  isCurrentPlan={isCurrentPlan}
                  secondaryAction={
                    isCurrentPlan && hasSubscription
                      ? { content: 'Cancel subscription', onClick: openCancelModal }
                      : null
                  }
                  t={t}
                  button={{
                    content: isCurrentPlan ? 'Current Plan' : card.buttonText,
                    props: {
                      variant: 'primary',
                      size: 'large',
                      loading: checkoutLoading === card.planName.toLowerCase(),
                      disabled: isCurrentPlan,
                      onClick: () => handleSubscribe(card.planName),
                    },
                  }}
                />
              );
            })}
          </div>
        </Box>

        {/* Last resort: a subscription on a plan no row claims as current.
            Not expected, but the alternative is a paying user with no way to
            stop paying. */}
        {hasSubscription && !currentPlanHasCancel && (
          <div className="flex justify-center">
            <Button variant="plain" tone="critical" onClick={openCancelModal}>
              Cancel subscription
            </Button>
          </div>
        )}

        {/* Starter (free) + custom staff */}
        <Box style={{ width: '100%', maxWidth: '100%', margin: '0 auto', marginBottom: '10px' }}>
          <div className="bg-white rounded-[1rem] border border-gray-200 shadow-sm overflow-hidden transform transition-all hover:shadow-md">
            <Box padding={{ xs: '400', md: '500' }}>
              <InlineStack align="space-between" blockAlign="center" gap="400">
                <div className="flex flex-1 items-center gap-6">
                  <div className="flex-none bg-[#FFF4E5] text-[#B95000] px-4 py-1.5 rounded-full text-sm font-bold border border-[#FFDDB3] shadow-sm">
                    Free Plan
                  </div>
                  <div className="hidden md:flex flex-1 justify-center gap-6">
                    {starter.features.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm font-medium text-slate-700">
                        <div className="bg-emerald-100 text-emerald-600 rounded-full p-0.5">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"></polyline></svg>
                        </div>
                        {f}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex-none">
                  <Button
                    size="large"
                    onClick={openDowngradeModal}
                    disabled={isFreeActive || hasSubscription}
                  >
                    {/* A subscriber clicking this used to cap themselves at one
                        seat while Stripe kept billing - activateFreePlan never
                        cancels the subscription. Their exit is either Cancel
                        subscription button, in the banner above or under their
                        current plan's card; both render only when
                        hasSubscription is true, so nothing is hidden. */}
                    {isFreeActive
                      ? 'Current Plan'
                      : hasSubscription
                        ? 'Cancel subscription to switch'
                        : 'Activate Free Plan'}
                  </Button>
                </div>
              </InlineStack>

              {/* Features - mobile */}
              <div className="md:hidden mt-4 space-y-3 pl-1">
                {starter.features.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm font-medium text-slate-700">
                    <div className="bg-emerald-100 text-emerald-600 rounded-full p-0.5">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"></polyline></svg>
                    </div>
                    {f}
                  </div>
                ))}
              </div>

              <div className="my-6 h-px bg-slate-100 w-full" />

              {/* Custom staff */}
              <div className="flex flex-col md:flex-row items-center justify-between gap-6 pl-1 pr-1">
                <div className="flex-1 w-full md:w-auto">
                  <Text variant="headingMd" as="h4" fontWeight="bold">Need More Staff?</Text>
                  <Text variant="bodySm" color="subdued">Scale your team with a custom plan ($6/staff/month).</Text>
                </div>

                <div className="flex-[2] w-full max-w-2xl px-2">
                  <div className="flex items-center gap-4 bg-slate-50 p-2 rounded-lg border border-slate-100">
                    <div className="px-2 text-xs font-bold text-slate-500 uppercase tracking-wide">Select Amount</div>
                    <div className="flex-1">
                      <RangeSlider
                        output
                        label="Custom Staff Count"
                        labelHidden
                        min={1}
                        max={50}
                        value={customStaffCount}
                        onChange={(val) => setCustomStaffCount(val)}
                        disabled={isCustomPlanActive}
                        suffix={<Text variant="bodyMd" fontWeight="bold" as="span">{customStaffCount} Staff{customStaffCount !== 1 ? 's' : ''}</Text>}
                      />
                    </div>
                    <div className="min-w-[40px] text-center font-bold text-slate-900 text-lg bg-white px-3 py-1 rounded border border-slate-200 shadow-sm">{customStaffCount}</div>
                  </div>
                </div>

                <div className="flex-none w-full md:w-auto">
                  <button
                    onClick={handleCustomSubscribe}
                    disabled={isCustomPlanActive || checkoutLoading === 'custom'}
                    className="w-full md:w-auto font-bold py-3 px-6 rounded-full transition-all shadow-lg flex items-center justify-center gap-2 group bg-slate-900 hover:bg-black text-white hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-lg"
                  >
                    <span>
                      {isCustomPlanActive
                        ? 'Current Plan'
                        : checkoutLoading === 'custom'
                          ? 'Starting checkout…'
                          : `Subscribe for $${(customStaffCount * 6).toFixed(2)}`}
                    </span>
                    {!isCustomPlanActive && (
                      <div className="bg-white/20 rounded-full p-1 group-hover:bg-white/30 transition-colors">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"></circle><path d="M12 6v6l4 2"></path></svg>
                      </div>
                    )}
                  </button>

                  {/* The custom plan says Current Plan here but matches none of
                      the three cards, so without this its subscribers would have
                      no cancel control anywhere on the page. */}
                  {isCustomPlanActive && (
                    <div className="mt-2 flex justify-center md:justify-end">
                      <Button variant="plain" tone="critical" onClick={openCancelModal}>
                        Cancel subscription
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Box>
          </div>
        </Box>
        {/* Proof and objection handling, below the plans: what other
            customers said, and the questions that stop a purchase. */}
        <PricingTrustFaq t={t} />
      </BlockStack>

      {/* Cancel confirmation ---------------------------------------------- */}
      <Modal
        open={cancelModalOpen}
        onClose={() => { if (!canceling) { setCancelModalOpen(false); setCancelError(null); } }}
        title="Cancel subscription?"
        primaryAction={{
          content: 'Yes, cancel subscription',
          destructive: true,
          loading: canceling,
          onAction: handleCancelSubscription,
        }}
        secondaryActions={[{
          content: 'Keep my plan',
          disabled: canceling,
          onAction: () => { setCancelModalOpen(false); setCancelError(null); },
        }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            {cancelError && <Banner tone="critical">{cancelError}</Banner>}
            <Text as="p" variant="bodyMd">
              Cancel your <Text as="span" fontWeight="bold">{currentPlanName}</Text> subscription and move
              to the Free plan?
            </Text>
            {/* cancelSubscription calls Stripe's immediate cancel, not
                cancel-at-period-end. Saying so is the difference between an
                informed decision and a support ticket. */}
            <Text as="p" variant="bodyMd" tone="subdued">
              This takes effect immediately — you won't be billed again, and paid access ends now
              rather than at the end of the current period.
            </Text>
            <Text as="p" variant="bodyMd" tone="subdued">
              You keep 1 staff seat. Everyone else stays in your account but can't clock in until you
              subscribe again.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Downgrade to Free confirmation ----------------------------------- */}
      <Modal
        open={downgradeOpen}
        onClose={() => { if (!activatingFree) { setDowngradeOpen(false); setDowngradeError(null); } }}
        title="Switch to the Free plan?"
        primaryAction={{
          content: 'Yes, switch to Free',
          destructive: true,
          loading: activatingFree,
          onAction: handleActivateFree,
        }}
        secondaryActions={[{
          content: 'Not now',
          disabled: activatingFree,
          onAction: () => { setDowngradeOpen(false); setDowngradeError(null); },
        }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            {downgradeError && <Banner tone="critical">{downgradeError}</Banner>}
            <Text as="p" variant="bodyMd">
              The Free plan covers <Text as="span" fontWeight="bold">1 staff member</Text>.
            </Text>
            <Text as="p" variant="bodyMd" tone="subdued">
              Anyone beyond the first stays in your account but can't clock in until you subscribe
              again. Nothing is deleted.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Box>
  );
};

export default Pricing;
